import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowUpRight, CalendarCheck, Flame } from "lucide-react";
import { Button } from "../components/ui/button";
import { SCHEDULE, WIX } from "../mock";
import Seo from "../components/Seo";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Matches labels like "Monday – Thursday", "Tuesday & Thursday", "Monday, Tuesday & Thursday"
const matchesDay = (label, day) => {
  if (label.includes("–") || label.includes("-")) {
    const [a, b] = label.split(/\s*[–-]\s*/).map((s) => DAYS.indexOf(s.trim()));
    const d = DAYS.indexOf(day);
    return a >= 0 && b >= 0 && d >= a && d <= b;
  }
  return label.split(/,|&/).map((s) => s.trim()).includes(day);
};

const Schedule = () => {
  const [day, setDay] = useState("All");

  const rows = SCHEDULE.map((row) => ({
    ...row,
    sessions: day === "All" ? row.sessions : row.sessions.filter((s) => matchesDay(s.day, day)),
  })).filter((row) => row.sessions.length > 0);

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [day]);

  return (
    <div className="pt-24">
      <Seo
        title="Class Schedule — Brazilian Jiu-Jitsu in Alameda, CA | Alameda BJJ"
        description="View our full weekly BJJ class schedule in Alameda for toddlers, kids, teens and adults. All levels welcome — your first class is free."
        path="/schedule"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Schedule", path: "/schedule" }]}
      />
      <section className="bg-[#F4F5F1] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Class Schedule</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] max-w-3xl leading-tight">
            Weekly Class Schedule in Alameda
          </h1>
          <p className="mt-5 text-neutral-600 max-w-2xl leading-relaxed">
            Classes for toddlers, kids, teens and adults — all levels welcome, no experience needed. Your first class is always free.
          </p>
          <Link to="/trial">
            <Button className="mt-6 h-12 px-8 bg-br-green hover:bg-[#00822f] text-white rounded-full font-semibold">
              <CalendarCheck size={18} className="mr-1.5" /> Book a Free Class
            </Button>
          </Link>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          {/* Day filter — sticky on mobile for easy navigation */}
          <div className="sticky top-[72px] z-30 -mx-5 px-5 py-3 mb-6 bg-white/95 backdrop-blur border-b border-neutral-100 lg:static lg:mx-0 lg:px-0 lg:border-0 lg:bg-transparent">
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="schedule-day-filter">
              {["All", ...DAYS].map((d) => (
                <button
                  key={d}
                  onClick={() => setDay(d)}
                  data-testid={`day-filter-${d.toLowerCase()}`}
                  className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    day === d ? "bg-br-green text-white" : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                  }`}
                >
                  {d === "All" ? "All days" : d.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>

          {rows.length === 0 && (
            <p className="text-center text-neutral-500 py-10" data-testid="schedule-empty">No classes on {day}. Pick another day or book a free class and we'll find you a time.</p>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            {rows.map((row, i) => (
              <div key={row.program} data-testid={`schedule-card-${row.program.toLowerCase().replace(/\s+/g, "-")}`} className="reveal bg-white rounded-2xl border border-neutral-100 p-6 sm:p-7 card-hover">
                <div className="flex items-center justify-between">
                  <h2 className="font-display text-xl font-bold text-[#1A1A1A]">{row.program}</h2>
                  <span className="text-xs font-semibold text-br-green bg-br-green/10 rounded-full px-3 py-1">{row.ages}</span>
                </div>
                <ul className="mt-5 space-y-3">
                  {row.sessions.map((s, j) => (
                    <li key={j} className="flex items-center justify-between border-b border-neutral-50 pb-3 last:border-0 last:pb-0">
                      <span className="flex items-center gap-2.5 text-neutral-700"><Clock size={16} className="text-br-green" /> {s.day}</span>
                      <span className="font-semibold text-[#1A1A1A] text-right flex flex-col items-end gap-1">
                        {s.time}
                        {s.popular && <span className="inline-flex items-center gap-1 rounded-full bg-[#ff7a00]/10 text-[#c75a00] text-[11px] font-bold px-2 py-0.5" data-testid="filling-fast-badge"><Flame size={11} /> Filling fast</span>}
                      </span>
                    </li>
                  ))}
                </ul>
                {row.booking && (
                  <a href={`${WIX.bookingBase}${row.booking}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="mt-5 w-full h-10 rounded-full border-neutral-300 font-semibold text-sm">
                      Book on Wix <ArrowUpRight size={14} className="ml-1" />
                    </Button>
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className="reveal mt-12 text-center bg-br-green rounded-2xl p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 h-40 w-40 bg-br-yellow/20 rounded-full blur-3xl" />
            <h3 className="font-display text-2xl font-bold text-white relative">Not Sure Which Class Fits?</h3>
            <p className="mt-3 text-green-50 relative">Book a free class and we'll help you find the perfect program for your age and goals.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center relative">
              <Link to="/trial"><Button className="h-12 px-8 bg-white text-br-green hover:bg-neutral-100 rounded-full font-semibold">Book a Free Class</Button></Link>
              <Link to="/pricing"><Button variant="outline" className="h-12 px-8 bg-transparent border-white text-white hover:bg-white hover:text-br-green rounded-full font-semibold">View Pricing</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Schedule;
